import { Link, createFileRoute, notFound } from '@tanstack/react-router';
import {
  ArrowLeft,
  ArrowRight,
  BarChart3,
  Check,
  ChevronDown,
  CircleDollarSign,
  FileSearch,
  LayoutTemplate,
  MapPin,
  MessageCircle,
  Search,
  ShieldCheck,
  Sparkles,
  Target,
} from 'lucide-react';

import { ContactSection } from '@/components/contact-section';
import { PublicSiteHeader } from '@/components/public-site-header';
import { canonicalSiteUrl } from '@/config';

import { whatsappUrl } from '@/lib/whatsapp';
const emailAddress = 'info@doqcreative.com';

type ServicePage = {
  slug: string;
  eyebrow: string;
  title: string;
  intro: string;
  metaTitle: string;
  metaDescription: string;
  accent: string;
  fit: string[];
  deliverables: string[];
  planning: Array<{ title: string; body: string }>;
  process: Array<{ title: string; body: string }>;
  faqs: Array<{ q: string; a: string }>;
};

type ServiceDecisionContent = {
  painIntro: string;
  painPoints: Array<{ title: string; body: string }>;
  comparison: Array<{ aspect: string; self: string; doq: string }>;
  cases: Array<{ title: string; body: string; slug: string }>;
};

const services: ServicePage[] = [
  {
    slug: 'web-design',
    eyebrow: 'Website design',
    title: '網頁設計',
    intro:
      '為香港公司設計清晰、好看、手機版順手的公司網站和服務網站。不是先砌版面，而是先把服務、內容次序、信任感和查詢路徑度清楚。',
    metaTitle: '香港網頁設計｜公司網站與服務網站製作｜渡橋',
    metaDescription:
      '渡橋為香港中小企提供網頁設計、公司網站、服務網站及 Landing Page 製作。先整理內容和查詢路徑，再設計清晰可信的網站。',
    accent: '清楚講服務',
    fit: ['想重做公司網站', '舊網站內容太散', '手機版查詢不順', '新品牌需要可信入口'],
    deliverables: [
      '網站架構和頁面規劃',
      '首頁、服務頁和必要內容頁設計',
      '手機版和桌面版介面',
      '基本 SEO 標題與頁面描述',
      'WhatsApp、電話或 Email 查詢入口',
    ],
    planning: [
      {
        title: '先分清網站任務',
        body: '公司網站、服務網站和 Landing Page 的重點不同。開始前先決定哪些頁面要建立信任，哪些頁面要承接查詢。',
      },
      {
        title: '內容次序比裝飾重要',
        body: '客人入站後要快速知道你做什麼、服務是否適合、為什麼可信、下一步怎樣聯絡。',
      },
      {
        title: '手機版優先處理',
        body: '很多本地查詢來自手機搜尋。按鈕位置、段落長度、圖片比例和載入速度都要一開始想。',
      },
    ],
    process: [
      { title: '瞭解業務', body: '瞭解服務、客人類型、競爭對手和現有網站問題。' },
      { title: '度橋規劃', body: '整理網站架構、主頁訊息、服務頁內容和查詢流程。' },
      { title: '設計製作', body: '設計桌面和手機版，撰寫或整理頁面文案。' },
      { title: '上線檢查', body: '檢查速度、基本 SEO、連結、表單或 WhatsApp 入口。' },
    ],
    faqs: [
      {
        q: '網頁設計會包文案嗎？',
        a: '可以協助整理文案。你不需要一開始準備完整內容，我們會先問清楚服務、客人問題和信任資料，再整理成網站能用的說法。',
      },
      {
        q: '可以只做公司網站，不做廣告嗎？',
        a: '可以。網站本身要先清楚可信，日後要做 SEO 或 Google Ads 時，也會比較容易承接流量。',
      },
      {
        q: '網站做好後可以自己更新嗎？',
        a: '視乎網站架構而定。常更新的內容，例如案例、文章或 FAQ，可以按需要做成比較容易管理的形式。',
      },
    ],
  },
  {
    slug: 'seo',
    eyebrow: 'SEO 服務｜搜尋優化',
    title: '香港 SEO 服務｜搜尋優化與網站規劃',
    intro:
      'SEO 服務不是只在網站上線前做一次規劃，而是持續檢查搜尋數據、優化服務頁和內容，令香港客人更容易在 Google 找到你，再把搜尋流量帶到清楚的查詢入口。',
    metaTitle: '香港 SEO 服務｜持續搜尋優化與網站規劃｜渡橋',
    metaDescription:
      '渡橋為香港公司提供 SEO 服務，包括持續搜尋優化、關鍵字研究、服務頁 SEO、內容與 FAQ、內部連結及 Search Console 檢查，逐步增加有效查詢。',
    accent: '讓客人搵到你',
    fit: ['想找持續 SEO 服務或代運營', '網站有內容但搜尋不到', '想做服務頁 SEO', '準備重整舊網站或改善查詢質量'],
    deliverables: [
      '關鍵字方向和搜尋意圖整理',
      '網站架構與服務頁分工',
      '頁面標題、描述和 H1 建議',
      '內容段落、FAQ 和內部連結建議',
      '持續 SEO 服務及每月優化方向',
      'Search Console 搜尋數據檢查',
    ],
    planning: [
      {
        title: '不是所有字都放首頁',
        body: '品牌字、服務字、地區字和問題字要分工。每一類搜尋背後的客人意圖不同，承接頁面也應該不同。',
      },
      {
        title: '先處理架構，再處理文章',
        body: '很多網站不是缺文章，而是服務頁本身說不清。主頁、服務頁、FAQ 和案例要先分好任務。',
      },
      {
        title: '持續優化，不是一份報告就完',
        body: 'SEO 服務會按搜尋表現和實際查詢，持續調整標題、內容、FAQ、內部連結和 CTA，而不是交完規劃文件便停止。',
      },
    ],
    process: [
      { title: '搜尋盤點', body: '整理你的服務、地區、客人問題和主要競爭對手。' },
      { title: '關鍵字分組', body: '把不同搜尋意圖分到首頁、服務頁或 Landing Page。' },
      { title: '頁面優化', body: '調整標題、段落、FAQ、內部連結和 CTA。' },
      { title: '持續追蹤優化', body: '按 Search Console、搜尋字詞和查詢數據，逐月改善頁面內容與轉化入口。' },
    ],
    faqs: [
      {
        q: 'SEO 服務包括甚麼？',
        a: 'SEO 服務包括關鍵字和搜尋意圖整理、服務頁優化、標題與內容調整、FAQ 和內部連結改善，以及按 Search Console 和查詢數據持續檢查。實際工作會按你的行業、地區和目標頁面安排。',
      },
      {
        q: 'SEO 幾耐會見效？',
        a: '視乎行業競爭、網站基礎和內容量。一般不會承諾幾日有排名，但可以先把網站結構和主要服務頁做好，讓後續累積更有方向。',
      },
      {
        q: '新網站一開始要做 SEO 嗎？',
        a: '最好要。新網站規劃時已經決定頁面分工、URL、標題和內容，比上線後再大改更有效率。',
      },
      {
        q: 'SEO 是否一定要寫 Blog？',
        a: '不一定。很多本地服務公司應該先做好主服務頁和 FAQ，再考慮文章內容。文章不是越多越好，要看搜尋意圖。',
      },
    ],
  },
  {
    slug: 'google-meta-ads',
    eyebrow: 'Google Ads / Meta 廣告服務',
    title: '香港 Google Ads / Meta 廣告服務｜投放與查詢追蹤',
    intro:
      '由 Google 搜尋廣告到 Meta 廣告，一併規劃 Landing Page、廣告訊息和查詢追蹤。服務涵蓋 FB 廣告、IG 廣告和 Threads 廣告，重點不是只買點擊，而是知道哪些受眾、素材和頁面真正帶來查詢。',
    metaTitle: '香港 Google Ads 與 Meta 廣告服務｜FB、IG、Threads 廣告投放｜渡橋',
    metaDescription:
      '渡橋為香港公司提供 Google Ads 及 Meta 廣告服務，包括 FB 廣告、IG 廣告、Threads 廣告、Landing Page、Meta Pixel 和查詢追蹤設定，讓每筆廣告預算更容易看清成效。',
    accent: '搜尋與社交廣告，都要有清楚承接',
    fit: ['想試 Google 搜尋廣告或 Meta 廣告', 'FB、IG 或 Threads 廣告有互動但少客問', '需要一頁清楚的 Landing Page', '想知道預算花在哪些關鍵字、受眾和素材'],
    deliverables: [
      '搜尋廣告帳戶和 Campaign 架構建議',
      '關鍵字、否定關鍵字和廣告文案方向',
      'Meta 廣告 Campaign、受眾、素材和訊息方向',
      'FB 廣告、IG 廣告和 Threads 廣告版位規劃',
      'Landing Page 內容和 CTA 規劃',
      'Meta Pixel、WhatsApp、電話或表單查詢追蹤',
      '按字詞、受眾、素材和頁面表現作調整',
      '廣告預算與投放節奏建議',
      '每月成效整理與優化建議',
    ],
    planning: [
      {
        title: '先確認流量落去哪',
        body: 'Google Ads 和 Meta 廣告不應該全部導到首頁。不同服務、地區、搜尋字、受眾或推廣重點，應該有清楚承接頁面。',
      },
      {
        title: '查詢追蹤要一開始設定',
        body: '如果沒有 Meta Pixel，也不知道哪些字、受眾或素材帶來 WhatsApp、電話或表單查詢，後面就只是在看點擊和曝光。',
      },
      {
        title: '預算先集中，不要太散',
        body: '中小企預算有限，初期應該先集中在最有商業意圖的搜尋字或最貼近客戶的 Meta 受眾，再慢慢擴展。',
      },
    ],
    process: [
      { title: '定推廣目標', body: '確認想推的服務、地區、預算、理想查詢類型和適合的廣告平台。' },
      { title: '規劃 Landing Page', body: '整理頁面訊息、信任元素、FAQ 和 CTA。' },
      { title: '設定廣告', body: '建立 Google 搜尋廣告或 Meta 廣告，設定關鍵字、受眾、素材、文案和追蹤。' },
      { title: '按數據優化', body: '看字詞、受眾、素材、成本、查詢質量和頁面表現再調整。' },
    ],
    faqs: [
      {
        q: 'Google Ads 或 Meta 廣告每月要很多預算嗎？',
        a: '不一定。預算要看行業競爭、服務價值和推廣目標。比起一開始花很多，更重要是先追蹤清楚，知道錢用在哪些關鍵字、受眾、素材和頁面。',
      },
      {
        q: '可以只做一頁 Landing Page 嗎？',
        a: '可以，尤其適合先測試某項服務。Landing Page 會集中處理客人問題、服務重點、信任資料和 WhatsApp 查詢。',
      },
      {
        q: '已有 Google Ads 或 Meta 廣告帳戶可以接手嗎？',
        a: '可以先檢視現有帳戶、關鍵字、受眾、素材、Meta Pixel 和轉化追蹤，再建議保留、重整或重新建立。',
      },
    ],
  },
  {
    slug: 'google-ads',
    eyebrow: 'Google Ads 廣告服務',
    title: '香港 Google Ads 廣告服務｜搜尋廣告代營運',
    intro: 'Google Ads 服務以客人正在搜尋的服務、地區和問題為起點，規劃關鍵字、廣告文案、Landing Page 和查詢追蹤。重點不是單看點擊，而是知道哪些搜尋字真正帶來合適查詢。',
    metaTitle: '香港 Google Ads 廣告服務｜搜尋廣告代營運｜渡橋',
    metaDescription: '渡橋為香港公司提供 Google Ads 搜尋廣告代營運，包括 Campaign 架構、關鍵字、否定關鍵字、廣告文案、Landing Page 與 WhatsApp、電話或表單查詢追蹤。',
    accent: '客人主動搜尋時，直接回應他的需要',
    fit: ['客人會主動搜尋服務、地區或價錢方向', '想測試哪些搜尋字帶來合適查詢', 'Google 廣告流量目前只回到首頁', '需要分清廣告媒體費、管理工作和查詢質素'],
    deliverables: ['Google Ads 帳戶與 Campaign 架構建議', '服務、地區和搜尋意圖的關鍵字分組', '否定關鍵字與廣告文案方向', '對應服務頁或 Landing Page 的內容與 CTA 建議', 'WhatsApp、電話或表單查詢追蹤檢查', '按搜尋字詞、成本、查詢質素和頁面表現持續調整', '預算分配、每月成效整理與優化建議'],
    planning: [
      { title: '先分清客人會搜尋甚麼', body: '品牌字、服務字、地區字和問題字的意圖不同，不應放在同一組廣告或同一個 Landing Page。' },
      { title: '搜尋字要有對應落地頁', body: '客人搜尋指定服務時，廣告和首屏要直接回應該需要，避免點入後仍要在首頁重新找資料。' },
      { title: '先定義何謂合適查詢', body: '除了點擊和表單數量，也要記錄電話、WhatsApp、服務範圍和查詢質素，才知道預算應加在哪裡。' },
    ],
    process: [
      { title: '盤點服務與搜尋意圖', body: '確認主推服務、地區、不承接項目、理想查詢和現有頁面。' },
      { title: '建立關鍵字與頁面結構', body: '把搜尋字分組，安排廣告文案、否定字和對應服務頁或 Landing Page。' },
      { title: '設定投放與追蹤', body: '建立 Campaign、廣告、轉化動作和查詢紀錄方式。' },
      { title: '按搜尋與查詢資料優化', body: '檢視搜尋字詞、成本、頁面表現和查詢內容，再調整預算與訊息。' },
    ],
    faqs: [
      { q: 'Google Ads 每月預算要幾多？', a: '沒有固定金額，應按服務價值、行業競爭、地區和所需查詢量安排。預算太分散時難以累積資料，通常先集中一至兩項服務與明確轉化目標較實際。' },
      { q: 'Google Ads 代營運費是否包括廣告費？', a: '一般不包括。Google 媒體費直接支付給平台，代營運費涵蓋帳戶架構、關鍵字、廣告、追蹤和持續優化；兩者會分開列明。' },
      { q: 'Google Ads 一定要有 Landing Page 嗎？', a: '不一定。品牌搜尋或服務資訊已很完整時可導向首頁或服務頁；推指定服務、地區或優惠時，通常以對應 Landing Page 承接會較清楚。' },
      { q: '已有 Google Ads 帳戶可以接手嗎？', a: '可以先檢視現有 Campaign、關鍵字、搜尋字詞、轉化設定和 Landing Page，再判斷哪些可保留、重整或新建。' },
      { q: 'Google Ads 需要設定甚麼轉化追蹤？', a: '最少要確認真正有商業價值的動作，例如 WhatsApp、電話或表單查詢；有條件時亦應記錄查詢後是否符合服務範圍，避免只以點擊或表單數量判斷。' },
      { q: '廣告帳戶應該由誰擁有？', a: '建議由客戶公司持有 Google Ads、GA4、Tag Manager 和網站的主要管理權限，代營運團隊按需要取得工作權限，日後交接和資料查核會較清楚。' },
      { q: 'Google Ads 需要多久調整一次？', a: '要視流量、預算和測試目的而定。搜尋字詞、否定字、預算、廣告訊息和 Landing Page 應按可用資料持續檢視，不應只在月尾看一次報告。' },
    ],
  },
  {
    slug: 'meta-ads',
    eyebrow: 'Meta 廣告服務',
    title: '香港 Meta 廣告服務｜FB、IG、Threads 廣告代營運',
    intro: 'Meta 廣告服務涵蓋 Facebook、Instagram 和 Threads，以受眾、素材、訊息和再營銷規劃為核心。適合主動讓潛在客人認識服務，再把互動和流量帶到清楚的 Landing Page 或查詢入口。',
    metaTitle: '香港 Meta 廣告服務｜FB、IG、Threads 廣告代營運｜渡橋',
    metaDescription: '渡橋為香港公司提供 Meta 廣告代營運，包括 Facebook、Instagram、Threads 廣告、受眾、素材方向、Meta Pixel、Landing Page 及 WhatsApp、電話或表單查詢追蹤。',
    accent: '把受眾、素材和下一步查詢接起來',
    fit: ['想以 FB、IG 或 Threads 主動接觸潛在客人', '有互動或瀏覽，但少人進一步查詢', '需要測試不同素材、受眾或服務訊息', '想處理再營銷與 Meta Pixel 查詢追蹤'],
    deliverables: ['Meta Campaign 目標與受眾架構建議', 'Facebook、Instagram 和 Threads 版位規劃', '廣告素材、文案和訊息角度建議', '冷受眾、互動受眾與再營銷方向', 'Landing Page、即時表單和 CTA 選擇建議', 'Meta Pixel、查詢追蹤與資料檢查', '按受眾、素材、版位、成本和查詢質素持續調整', '預算節奏、每月成效整理與優化建議'],
    planning: [
      { title: '先確定廣告要解決哪個階段', body: '新服務需要先被理解，熟悉受眾可直接查詢，曾到訪網站的人則適合再營銷；不同階段不應只用同一個廣告。' },
      { title: '素材要測試訊息，不只換顏色', body: '可比較客人問題、服務好處、案例、流程或優惠等不同角度，再根據查詢質素而不是單純互動判斷。' },
      { title: '平台數據要對照實際查詢', body: 'Meta Pixel 有助比較廣告，但 WhatsApp、電話和表單查詢仍要記錄服務需要與質素，避免只看平台內數字。' },
    ],
    process: [
      { title: '確認受眾與推廣目標', body: '整理客人情境、服務訊息、地區、素材和主要轉化動作。' },
      { title: '規劃素材與承接頁面', body: '安排廣告訊息、圖片或影片方向、Landing Page 或即時表單的下一步。' },
      { title: '設定 Campaign 與追蹤', body: '建立受眾、版位、預算、Meta Pixel 和 WhatsApp、電話或表單追蹤。' },
      { title: '按素材與查詢質素優化', body: '比較受眾、素材、版位、頻率、成本和實際查詢內容，再調整投放。' },
    ],
    faqs: [
      { q: 'Meta 廣告是否只適合賣產品？', a: '不是。本地服務、專業服務和新品牌都可用 Meta 廣告建立認識、展示案例、收集查詢或進行再營銷；重點是服務是否能用清楚素材和下一步解釋。' },
      { q: 'FB、IG 和 Threads 需要分開投放嗎？', a: '要視受眾、素材形式和結果而定。可先用合適版位測試，再按查詢質素、素材表現和預算安排決定是否分開管理。' },
      { q: 'Meta 廣告流量應該去網站還是即時表單？', a: '簡單資料收集可測試即時表單；需要說明服務、案例、價錢方向或信任資料時，手機易讀的 Landing Page 通常較合適。' },
      { q: 'Meta 廣告代營運費是否包括廣告媒體費與素材拍攝？', a: '一般會分開列出。媒體費支付給 Meta；策略與帳戶管理、平面設計、影片拍攝或剪輯是不同工作，正式報價會確認各自範圍。' },
      { q: 'Meta 廣告需要每月準備多少素材？', a: '沒有固定數量，取決於受眾大小、投放預算、服務複雜度和測試目標。重點是準備真正不同的訊息角度，再按受眾與查詢質素決定是否延伸。' },
      { q: '沒有 Meta Pixel 可以投放嗎？', a: '可以開始投放，但若目標是網站查詢，應盡早設定 Pixel 和主要事件，並以 WhatsApp、電話或表單的實際記錄補足平台數據。' },
      { q: 'Meta 廣告可以同時配合 Google Ads 嗎？', a: '可以。Google Ads 承接正在搜尋服務的人，Meta 可用作建立認識、測試素材和再營銷；兩者應各自量度媒體費與查詢，同時共用清楚的服務頁。' },
    ],
  },
  {
    slug: 'erp-system-development',
    eyebrow: 'ERP / 營運系統訂製',
    title: '香港 ERP 系統訂製',
    intro:
      '將客戶、報價、訂單、庫存、項目、員工、出勤、更表、出糧資料、審批和報表，按你公司實際流程整合成一套營運系統。先釐清工作方法，再由原型、開發、測試到上線及持續迭代逐步完成。',
    metaTitle: '香港 ERP 系統訂製｜中小企營運系統開發｜渡橋',
    metaDescription:
      '渡橋為香港中小企訂製 ERP 營運系統，整合客戶、報價、訂單、庫存、項目、員工、出勤、更表、出糧資料、審批、報表及第三方系統；按流程、角色、整合和資料遷移範圍報價。',
    accent: '流程清楚，系統才真正好用',
    fit: ['仍靠 Excel、WhatsApp 或多個軟件交接資料', '報價、訂單、庫存、項目或員工資料經常重複輸入', '需要處理出勤、更表、出糧資料或管理審批', '不同角色需要不同權限、報表或持續迭代'],
    deliverables: [
      '現有流程、角色和資料流梳理',
      '功能清單、權限設計與可點擊原型',
      '客戶、報價、訂單、庫存、項目、員工、出勤、更表、出糧資料、審批或報表模組訂製',
      '第三方 API、付款、短信、電郵或現有系統整合規劃',
      '測試、上線、交付說明及持續功能迭代安排',
    ],
    planning: [
      {
        title: '先梳理流程，不急於選功能',
        body: '先看每個角色怎樣收資料、做決定和交接，才判斷哪些步驟應由系統處理，避免把舊問題搬進新系統。',
      },
      {
        title: '先做核心模組，再逐步擴充',
        body: '先處理最影響營運的報價、訂單、庫存或項目流程，再按實際使用情況加入審批、報表和整合。',
      },
      {
        title: '權限、資料和交接要先定規則',
        body: '誰可以看、改、批核或匯出資料會直接影響系統是否可用；上線前要一併確認資料遷移和日後維護方式。',
      },
    ],
    process: [
      { title: '需求與流程分析', body: '瞭解目前工具、角色、資料來源、交接方式和真正要解決的營運問題。' },
      { title: '確認範圍與原型', body: '整理功能優先次序、權限、資料欄位和畫面原型，確認第一階段交付內容。' },
      { title: '開發、測試與上線', body: '按確認範圍開發核心模組，與團隊測試流程、資料和權限後安排上線。' },
      { title: '持續迭代', body: '根據實際使用情況處理培訓、功能調整、第三方整合和下一階段需求。' },
    ],
    faqs: [
      {
        q: 'ERP 系統訂製是否只適合大型公司？',
        a: '不一定。中小企只要已有重複輸入、資料分散、多人交接或報價訂單難跟進的情況，就可以由一兩個核心流程開始，不需要一次過做完整大系統。',
      },
      {
        q: '可以由 Excel 或現有軟件遷移資料嗎？',
        a: '可以先盤點資料格式、重複資料、歷史資料和需要保留的欄位，再確認資料清理、匯入和驗證範圍。資料遷移會按實際複雜度列入報價。',
      },
      {
        q: 'ERP 系統訂製怎樣報價？',
        a: '按功能模組、用戶角色、權限、第三方整合、資料遷移、測試和上線安排報價。雲端、短信、付款、軟件授權或 API 等第三方費用會另行列明。',
      },
      {
        q: '完成系統後還可以新增功能嗎？',
        a: '可以。交付後可按實際使用情況安排持續功能迭代，例如新增報表、審批規則、整合或角色權限；每個階段會先確認範圍再安排開發。',
      },
    ],
  },
  {
    slug: 'local-service-website',
    eyebrow: 'Local service website',
    title: '本地服務網站設計',
    intro:
      '適合維修、工程、美容、清潔、教育、到會等香港本地服務。頁面要讓客人很快看清服務範圍、地區、收費方向和查詢方法，手機版尤其要順手。',
    metaTitle: '本地服務網站設計｜香港中小企服務網站製作｜渡橋',
    metaDescription:
      '渡橋為香港本地服務公司設計網站，整理服務範圍、地區頁、收費方向、FAQ 及 WhatsApp 查詢入口，適合維修、工程、清潔、美容及教育服務。',
    accent: '服務範圍要一眼清楚',
    fit: ['客人通常先搜地區再搵公司', '服務範圍和收費方式要講清楚', '想減少只問一句就消失的查詢', '需要客人影相或留資料再報價'],
    deliverables: [
      '首頁與主要服務頁設計',
      '服務範圍、地區和收費方向整理',
      '手機版 WhatsApp 查詢入口',
      'FAQ、流程和信任資料排版',
      '基本本地 SEO 標題與內容方向',
    ],
    planning: [
      {
        title: '先講清楚你做邊類服務',
        body: '本地服務網站最怕講得太闊。頁面要清楚列出服務項目、適合情況和不包括的範圍，減少無效查詢。',
      },
      {
        title: '地區和收費要有方向',
        body: '不一定要公開完整價目表，但客人至少要知道服務地區、收費怎樣計，以及查詢前需要準備什麼資料。',
      },
      {
        title: '手機版要少一步',
        body: '很多客人是在街外或辦公室用手機搜尋。按鈕、電話、WhatsApp 和表單要放在容易看到的位置。',
      },
    ],
    process: [
      { title: '整理服務', body: '分清主要服務、附加服務、地區範圍和常見客人問題。' },
      { title: '規劃頁面', body: '決定首頁、服務頁、FAQ、案例或地區內容怎樣分工。' },
      { title: '設計製作', body: '製作桌面和手機版，整理清楚直接的服務文案。' },
      { title: '上線檢查', body: '檢查 WhatsApp、電話、SEO 標題、速度和手機閱讀體驗。' },
    ],
    faqs: [
      {
        q: '本地服務網站一定要做很多頁嗎？',
        a: '不一定。服務單一可以先做一頁；服務和地區較多，才需要拆成多個服務頁或地區頁。',
      },
      {
        q: '可以加入收費表嗎？',
        a: '可以。如果價格會按情況變動，也可以改成收費方向、估價因素和查詢前準備資料，避免客人誤會。',
      },
      {
        q: '網站可以配合 Google 搜尋廣告嗎？',
        a: '可以。常見做法是先做清楚的服務頁或 Landing Page，再把廣告流量導到最貼近搜尋字的頁面。',
      },
    ],
  },
  {
    slug: 'professional-service-website',
    eyebrow: 'Professional service website',
    title: '專業服務公司網站設計',
    intro:
      '適合顧問、會計、法律、醫療、B2B 服務和各類專業團隊。網站重點不是把履歷堆滿，而是讓客人看懂你處理什麼問題、流程怎樣走、為什麼可以信任。',
    metaTitle: '專業服務公司網站設計｜顧問、會計、法律、B2B 網站｜渡橋',
    metaDescription:
      '渡橋為香港專業服務公司設計網站，整理服務架構、團隊介紹、流程、案例、FAQ 和查詢路徑，適合顧問、會計、法律、醫療及 B2B 公司。',
    accent: '信任感要有根有據',
    fit: ['服務需要客人先建立信任', '想整理團隊和專業背景', '服務項目多但說法分散', '需要公司網站重整'],
    deliverables: [
      '公司網站架構與服務分類',
      '團隊、流程、案例和信任內容設計',
      '專業服務頁文案整理',
      '手機版與桌面版介面',
      '查詢 CTA、FAQ 和基本 SEO 設定',
    ],
    planning: [
      {
        title: '專業不是寫得越深越好',
        body: '客人通常先想知道你是否懂他的情況。網站要把服務範圍、處理方式和適合對象講清楚，再放資歷和細節。',
      },
      {
        title: '信任資料要放在對的位置',
        body: '團隊介紹、案例、客戶類型、流程和常見問題都可以建立信任，但要跟著客人閱讀次序出現。',
      },
      {
        title: '不同服務要拆清楚',
        body: '如果所有服務都放在一頁，客人和 Google 都難理解。重要服務應該有自己的頁面和查詢入口。',
      },
    ],
    process: [
      { title: '瞭解業務', body: '整理服務線、客戶類型、決策流程和現有內容。' },
      { title: '重排架構', body: '規劃首頁、關於、服務、案例、FAQ 和聯絡頁的分工。' },
      { title: '設計內容', body: '把專業內容寫成客人容易讀懂的頁面，並保持品牌質感。' },
      { title: '上線優化', body: '檢查手機版、SEO 基礎、查詢入口和頁面速度。' },
    ],
    faqs: [
      {
        q: '專業服務網站需要放很多團隊資料嗎？',
        a: '視乎行業。客人需要信任你，但不一定要看很長履歷。通常會保留最有用的資歷、經驗和服務方式。',
      },
      {
        q: '如果服務比較複雜，文案怎樣處理？',
        a: '會先拆服務分類，再把每項服務寫成客人能理解的問題、做法、流程和下一步，而不是直接搬公司簡介。',
      },
      {
        q: '可以做中英文或多語言版本嗎？',
        a: '可以。多語言版本會按內容量報價，也會留意不同語言的頁面標題和 SEO 設定。',
      },
    ],
  },
  {
    slug: 'google-ads-landing-page',
    eyebrow: 'Google Ads / Meta 廣告落地頁',
    title: 'Google / Meta 廣告落地頁設計',
    intro:
      '為 Google Ads、Meta 廣告、短期推廣或單一服務而設計的 Landing Page。重點是讓客人點入來後不用慢慢找，直接看到服務、信任資料、FAQ 和查詢入口。',
    metaTitle: 'Google 與 Meta 廣告落地頁設計｜香港 Landing Page 製作｜渡橋',
    metaDescription:
      '渡橋為香港公司製作 Google Ads 及 Meta 廣告 Landing Page，整理單一服務賣點、查詢 CTA、FAQ、Meta Pixel、轉化追蹤和手機版頁面，讓 Google、FB、IG 或 Threads 廣告流量都有清楚承接。',
    accent: '一頁集中處理一個目標',
    fit: ['想推一項指定服務', '準備投放 Google Ads 或 Meta 廣告', '廣告有點擊但查詢少', '想先測試市場反應'],
    deliverables: [
      '單頁 Landing Page 架構',
      '廣告關鍵字與頁面訊息配合',
      '服務重點、FAQ 和信任資料整理',
      'WhatsApp、電話或表單 CTA',
      '基本查詢追蹤和 SEO 標題設定',
    ],
    planning: [
      {
        title: '一頁只處理一個主要服務',
        body: 'Landing Page 不適合塞太多服務。搜尋字、頁面標題、內容和 CTA 要集中，客人才容易行下一步。',
      },
      {
        title: '廣告字和頁面內容要對得上',
        body: '如果客人搜尋冷氣維修，頁面就不應該先講整間公司的所有業務。內容要直接回應搜尋意圖。',
      },
      {
        title: '追蹤要早點設定',
        body: 'WhatsApp 點擊、電話、表單和主要按鈕都應該一開始安排好，否則很難知道廣告是否值得加預算。',
      },
    ],
    process: [
      { title: '定推廣項目', body: '確認服務、客人痛點、地區、預算和理想查詢類型。' },
      { title: '寫頁面框架', body: '安排首屏、服務內容、信任資料、FAQ 和 CTA 次序。' },
      { title: '製作頁面', body: '設計桌面和手機版，配合廣告訊息和查詢路徑。' },
      { title: '上線追蹤', body: '檢查按鈕、速度、轉化事件和基本廣告承接情況。' },
    ],
    faqs: [
      {
        q: 'Landing Page 和普通服務頁有什麼不同？',
        a: 'Landing Page 會更集中，通常只推一項服務或一個優惠。普通服務頁則可以承擔長期 SEO 和公司網站內容。',
      },
      {
        q: '可以一星期內完成嗎？',
        a: '如果服務方向清楚、資料齊全，單頁 Landing Page 通常可以一星期內完成。開始前會先列清楚需要的文字、圖片和追蹤設定。',
      },
      {
        q: '一定要同時開 Google Ads 或 Meta 廣告嗎？',
        a: '不一定。可以先完成頁面，再決定是否投放 Google Ads 或 Meta 廣告；也可以用來放在社交平台、WhatsApp 或 Email 推廣。',
      },
    ],
  },
  {
    slug: 'whatsapp-chatbot-development',
    eyebrow: 'WhatsApp Chatbot 開發',
    title: '香港 WhatsApp Chatbot 開發｜自動回覆、預約及人工轉接',
    intro: 'WhatsApp Chatbot 開發會把常見問題、客戶資料收集、預約規則和人工客服轉接整理成清楚的查詢流程。重點不是代替所有同事，而是先處理重複問題，再把需要判斷的查詢交回合適的人。',
    metaTitle: '香港 WhatsApp Chatbot 開發｜自動回覆、預約及人工轉接｜渡橋',
    metaDescription: '渡橋提供香港 WhatsApp Chatbot 開發服務，包括自動回覆、FAQ、客戶資料收集、預約流程、人工轉接及可行的日曆或 CRM 整合，按實際範圍報價。',
    accent: '讓每次查詢有清楚的下一步',
    fit: ['WhatsApp 經常收到重複服務、價錢或營業時間問題', '團隊需要先收集地區、服務類型、人數或可預約時段', '預約靠人手來回確認，容易漏訊息或重複安排', '複雜查詢仍要由真人處理，但需要先分流和保留資料'],
    deliverables: ['現有 WhatsApp 查詢、FAQ 與人工交接流程梳理', '自動回覆、選單、資料收集與資格篩選對話設計', '預約規則、可約時段、確認與改期流程設計', '真人客服轉接條件、通知與查詢資料交接', 'WhatsApp Business API、日曆或 CRM 可行性與整合範圍確認', '測試情境、上線檢查、使用說明與後續迭代建議'],
    planning: [
      { title: '先分清哪些問題適合自動處理', body: '服務範圍、營業時間、準備資料和基本預約問題適合先回答；涉及複雜報價、例外情況或投訴時，應設計清楚的真人接手入口。' },
      { title: '預約不是只放一個日期選擇器', body: '要先確認服務地點、人員、時段、緩衝時間、取消或改期規則，才可判斷日曆或預約系統需要怎樣接駁。' },
      { title: '資料要交到正確的人手上', body: '每段對話收集甚麼資料、誰會收到通知、怎樣查看歷史紀錄，都要在上線前確認，避免自動化後反而漏跟進。' },
    ],
    process: [
      { title: '梳理查詢與預約流程', body: '整理常見問題、客戶資料、服務規則、真人接手條件和現有工具。' },
      { title: '設計對話與整合範圍', body: '確定自動回覆、選單、預約規則、通知、日曆或 CRM 的第一階段範圍。' },
      { title: '配置、測試與調整', body: '按真實情境測試錯誤輸入、改期、人工轉接和資料交接，再調整對話內容。' },
      { title: '上線與持續優化', body: '上線後按實際查詢、漏答問題和團隊回覆方式，安排後續內容或功能迭代。' },
    ],
    faqs: [
      { q: 'WhatsApp Chatbot 開發可以自動回答哪些問題？', a: '適合先處理服務範圍、營業時間、地點、基本價錢方向、需要準備的資料、預約和常見 FAQ。涉及複雜判斷、正式報價、投訴或例外情況時，應安排真人客服接手。' },
      { q: '是否一定要接 Google Calendar 或 CRM？', a: '不一定。先把對話和預約規則理清楚，再確認現有日曆或 CRM 是否有可用 API、帳戶權限和實際整合價值。第三方整合會在正式報價獨立列出。' },
      { q: 'Chat Bot 可以代替真人客服嗎？', a: '不應把所有查詢都交給自動回覆。較合適的做法是由 Chat Bot 處理重複問題、收集必要資料和分流，真人負責複雜情況、報價確認和關系跟進。' },
      { q: 'WhatsApp Chatbot 開發怎樣報價？', a: '按對話流程數量、語言、資料收集、預約規則、真人轉接、第三方整合、測試和上線支援確認。WhatsApp Business API、雲端、CRM 或日曆等持續費用會另行列明。' },
      { q: '客戶已有 WhatsApp Business 帳戶可以使用嗎？', a: '會先確認現有帳戶、號碼、Meta Business 管理權限和所需 API 資格，再決定遷移、連接或保留現有處理方式。' },
      { q: '上線後可以再加功能嗎？', a: '可以。先上線最常用的 FAQ、資料收集或預約流程，再按真實對話補充服務分流、通知、日曆或 CRM 整合，通常較容易控制範圍。' },
    ],
  },
  {
    slug: 'website-redesign',
    eyebrow: '網站改版',
    title: '網站改版｜舊網站重整與 SEO 保留',
    intro:
      '網站改版適合已有網站但內容太散、手機版難用、查詢入口不清楚，或者 SEO 和廣告承接做得不順的公司。改版不是全盤推倒，而是先分清哪些要保留、哪些要重寫。',
    metaTitle: '舊網站重整｜公司網站改版、內容重排與 SEO 基礎｜渡橋',
    metaDescription:
      '渡橋為香港公司提供網站改版與舊網站重整服務，檢視現有網站內容、手機版、SEO、查詢流程和頁面架構，重新設計更清晰可信的公司網站。',
    accent: '保留有用的，重整混亂的',
    fit: ['舊網站多年未改', '內容太散或太舊', '手機版不好用', 'SEO 和廣告流量接不住'],
    deliverables: [
      '現有網站內容和頁面盤點',
      '新網站架構與內容次序規劃',
      '首頁、服務頁和查詢流程重整',
      '手機版介面重新設計',
      'SEO 標題、URL 和轉址建議',
    ],
    planning: [
      {
        title: '先看舊網站有什麼可用',
        body: '有些內容可以保留，例如案例、服務資料、相片和舊文章；有些則要重寫或刪走。先盤點，避免白做。',
      },
      {
        title: '重整頁面次序',
        body: '舊網站常見問題是所有資料都擠在一起。重整時會重新安排主頁、服務頁、案例、FAQ 和聯絡入口。',
      },
      {
        title: '不要弄丟原有 SEO',
        body: '如果舊網站已有排名或流量，改版時要留意 URL、標題、轉址和重要內容，不應該直接刪掉。',
      },
    ],
    process: [
      { title: '網站檢視', body: '檢查現有頁面、內容、手機版、速度、SEO 和查詢入口。' },
      { title: '定改版範圍', body: '分清需要重做、可以保留和之後再改善的部分。' },
      { title: '重新設計', body: '重排網站架構、頁面內容、視覺和手機版體驗。' },
      { title: '上線交接', body: '處理基本 SEO、轉址、追蹤、連結和上線前檢查。' },
    ],
    faqs: [
      {
        q: '舊網站重整一定要整個重做嗎？',
        a: '不一定。可以先重做首頁、主要服務頁或 Landing Page。若技術和內容都太舊，才建議整站重做。',
      },
      {
        q: '舊網站內容可以搬過來嗎？',
        a: '可以，但不會原封不動照搬。會先看哪些內容對客人和 SEO 有用，再重新整理次序和說法。',
      },
      {
        q: '改版會影響 SEO 嗎？',
        a: '有可能，所以改版前要先盤點重要頁面和 URL。需要時會安排轉址和保留重要內容，減少不必要的流量波動。',
      },
    ],
  },
];

const serviceDecisionContent: Record<string, ServiceDecisionContent> = {
  'web-design': {
    painIntro: '很多網頁設計項目卡住，不是因為顏色未選好，而是客人需求、內容次序和網站任務未先分清。',
    painPoints: [
      { title: '先選版面，後補內容', body: '做到一半才發現沒有足夠資料，或者重要服務放不到合適位置。' },
      { title: '所有服務擠在首頁', body: '客人和 Google 都難分辨不同服務，查詢時亦要再問一次基本資料。' },
      { title: '桌面好看，手機難用', body: '文字過長、按鈕太遠和圖片太重，都會令手機客人放棄下一步。' },
    ],
    comparison: [
      { aspect: '內容方向', self: '自己先找模板，再把公司資料逐段放入', doq: '先整理客人問題、服務次序和每頁任務' },
      { aspect: '手機體驗', self: '完成桌面版後再縮小調整', doq: '一開始同步規劃手機閱讀和查詢路徑' },
      { aspect: '上線準備', self: '逐項研究 SEO、速度、追蹤和寄存', doq: '按已確認範圍完成基本設定與上線檢查' },
    ],
    cases: [
      { title: '舒怡機電', slug: 'shu-yee-engineering', body: '重整公司形象、服務介紹和手機閱讀次序，示範多項服務怎樣拆成清楚架構。' },
      { title: '正EV', slug: 'plus-ev', body: '為新服務先講清楚資料，再建立承接搜尋、廣告和社交平台查詢的網站入口。' },
    ],
  },
  seo: {
    painIntro: 'SEO 沒有效果，常見原因不是完全沒有內容，而是搜尋問題、頁面分工和持續更新沒有連起來。',
    painPoints: [
      { title: '所有關鍵字放首頁', body: '不同搜尋意圖互相競爭，客人入站後亦看不到最貼近需要的內容。' },
      { title: '只寫文章，不補服務頁', body: '有資訊流量但欠缺清楚的服務、信任資料和查詢入口。' },
      { title: '上線後不再檢查', body: '沒有看 Search Console、索引和查詢變化，就無法知道下一步應改哪頁。' },
    ],
    comparison: [
      { aspect: '關鍵字', self: '按搜尋量挑字，再集中放在首頁', doq: '按搜尋意圖分配到服務頁、案例和文章' },
      { aspect: '內容執行', self: '想到題目才寫，頁面之間缺少連結', doq: '先定優先頁面，再按數據持續補內容' },
      { aspect: '成效判斷', self: '只看個別排名或總流量', doq: '同時看曝光、點擊、合適查詢和頁面表現' },
    ],
    cases: [
      { title: '文仔清拆', slug: 'man-demolition', body: '把住宅、寫字樓、商舖及餐廳清拆分清，讓不同服務搜尋有較明確的內容承接。' },
      { title: '文仔水喉', slug: 'man-plumber', body: '以緊急維修需要為核心，清楚交代服務範圍、收費方向和直接聯絡方法。' },
    ],
  },
  'google-ads': {
    painIntro: 'Google Ads 有點擊卻沒有合適查詢，常見原因是關鍵字意圖、廣告訊息、承接頁面和追蹤沒有對上。',
    painPoints: [
      { title: '所有搜尋字都回到首頁', body: '客人搜尋指定服務或地區後，仍要自行找資料，廣告承諾容易被稀釋。' },
      { title: '沒有排除不合適搜尋', body: '只加關鍵字、不看搜尋字詞，預算可能花在不屬於服務範圍或意圖很弱的流量。' },
      { title: '只看點擊，不看查詢內容', body: '點擊成本低未必等於有效；還要看電話、WhatsApp、表單及後續查詢是否合適。' },
    ],
    comparison: [
      { aspect: '關鍵字', self: '把所有服務和地區字混在同一組廣告', doq: '按服務、地區與搜尋意圖分組，並安排否定字' },
      { aspect: '落地頁', self: '一律導向首頁，再由客人自行尋找服務', doq: '讓廣告訊息、服務頁首屏、FAQ 和 CTA 對應' },
      { aspect: '優化依據', self: '只比較曝光、點擊和平均 CPC', doq: '同時看搜尋字詞、轉化、查詢質素和頁面表現' },
    ],
    cases: [
      { title: '文仔水喉', slug: 'man-plumber', body: '緊急維修客人需要立即看見服務範圍、收費方向和聯絡方法，適合承接高意圖搜尋流量。' },
      { title: '文仔冷氣', slug: 'man-aircon', body: '將安裝、維修、清洗和保養分開，讓不同搜尋服務可對應較清楚的廣告與頁面。' },
    ],
  },
  'meta-ads': {
    painIntro: 'Meta 廣告有互動、觸及或影片觀看，但未有合適查詢時，問題通常在受眾、素材訊息、承接頁面或後續追蹤。',
    painPoints: [
      { title: '只追 Like 和瀏覽', body: '互動成本低不代表客人會查詢，還要確認廣告是否交代服務、地區、信任和下一步。' },
      { title: '素材只換顏色和尺寸', body: '同一個訊息反覆投放很難學到甚麼；應比較不同客人問題、好處、證明或案例角度。' },
      { title: '沒有分冷受眾和再營銷', body: '第一次接觸服務的人與曾瀏覽網站的人需要不同訊息，混在一起會令預算和判斷變得模糊。' },
    ],
    comparison: [
      { aspect: '受眾策略', self: '所有人看同一套銷售訊息', doq: '按冷受眾、互動受眾和再營銷階段安排內容' },
      { aspect: '素材測試', self: '只換顏色、尺寸或一兩個字', doq: '比較問題、好處、流程、證明和案例等不同角度' },
      { aspect: '成效判斷', self: '只看平台顯示的互動或轉化', doq: '對照 Pixel、WhatsApp、電話、表單和實際查詢質素' },
    ],
    cases: [
      { title: '正EV', slug: 'plus-ev', body: '新服務需要先把概念、適合對象和查詢下一步講清楚，才較適合承接社交廣告流量。' },
      { title: '文仔清拆', slug: 'man-demolition', body: '具體服務範圍、報價方向和 WhatsApp 入口，能減少社交廣告訪客再找基本資料的步驟。' },
    ],
  },
  'google-meta-ads': {
    painIntro: '廣告有曝光和點擊但沒有合適查詢，問題往往出在受眾、訊息、落地頁和追蹤之間沒有對上。',
    painPoints: [
      { title: '所有廣告都回到首頁', body: '客人點入後要重新找服務，廣告承諾和頁面內容亦容易不一致。' },
      { title: '沒有記錄查詢來源', body: '只看到點擊和表單數量，卻不知道電話、WhatsApp 或查詢質素。' },
      { title: '太早只追最低成本', body: '便宜點擊或互動未必來自真正需要服務的人，還要看查詢內容。' },
    ],
    comparison: [
      { aspect: '投放前', self: '先開廣告，再決定要帶去哪一頁', doq: '先確認服務、客人、落地頁和轉化動作' },
      { aspect: '投放中', self: '主要看曝光、點擊或互動數字', doq: '檢查搜尋字、受眾、素材、查詢和頁面表現' },
      { aspect: '調整方式', self: '查詢少便直接加預算或換圖片', doq: '先找出是流量、訊息、頁面還是追蹤問題' },
    ],
    cases: [
      { title: '正EV', slug: 'plus-ev', body: '新服務網站把服務資料和查詢入口集中，適合作為搜尋或社交廣告的清楚承接頁面。' },
      { title: '文仔冷氣', slug: 'man-aircon', body: '把安裝、維修、清洗及保養服務分清，方便廣告按指定服務帶客人到相關內容。' },
    ],
  },
  'whatsapp-chatbot-development': {
    painIntro: '不是每條 WhatsApp 都需要真人從頭回答。重複查詢、預約確認和資料交接混在同一個對話裡，團隊越忙越容易漏答、漏約或忘記跟進。',
    painPoints: [
      { title: '重複問題佔去回覆時間', body: '服務範圍、營業時間、地點和基本價錢方向每天重複出現，但沒有一套一致的初步回覆。' },
      { title: '預約靠來回訊息確認', body: '時段、地點、人數和服務規則分散在對話裡，改期或交接時容易出現遺漏。' },
      { title: '真人接手時沒有資料脈絡', body: '同事收到新對話時仍要重新問一遍需求，客戶已經提供的資料沒有被清楚整理。' },
    ],
    comparison: [
      { aspect: '重複問題', self: '每次由同事重新輸入或複製舊答案', doq: '由訂製流程先回答一致 FAQ，再保留真人處理例外情況' },
      { aspect: '預約資料', self: '日期、地點和要求散在不同對話中', doq: '按規則逐項收集資料、確認預約，再交給團隊或可用日曆' },
      { aspect: '人工交接', self: '客戶要重複描述，團隊自行翻看聊天記錄', doq: '按觸發條件通知指定同事，並附上已收集的查詢資料' },
    ],
    cases: [],
  },
  'erp-system-development': {
    painIntro: '營運問題通常不是少了一個 App，而是資料散在 Excel、WhatsApp 和不同軟件，團隊每日仍靠人手追問、複製和核對。',
    painPoints: [
      { title: '資料分散又重複輸入', body: '客戶、報價、訂單、員工出勤和出糧資料分在不同檔案，更新一次仍要通知多個人。' },
      { title: '流程靠經驗，交接容易漏', body: '誰跟進、誰批核、下一步做甚麼沒有共用記錄，新同事尤其難掌握。' },
      { title: '報表到月尾才人手整理', body: '管理層想看訂單、庫存、項目或回款狀況時，團隊仍要花時間合併資料。' },
    ],
    comparison: [
      { aspect: 'Excel / WhatsApp', self: '彈性快，但多人更新、出勤更表、權限和版本難管理', doq: '適合初期或少量固定記錄' },
      { aspect: '現成 SaaS', self: '可較快開始，但流程要配合平台設定', doq: '適合標準化流程和常見功能' },
      { aspect: 'ERP 系統訂製', self: '按公司流程、角色和整合需要設計', doq: '適合有特定工作方式、資料流或持續擴充需要' },
    ],
    cases: [
      { title: '營運流程先梳理', slug: 'shu-yee-engineering', body: 'ERP 項目應先把不同服務、角色與交接資料分清；先有清楚架構，才知道哪些流程值得系統化。' },
      { title: '由查詢到交付的資料流', slug: 'man-aircon', body: '客戶資料、報價、工作安排和後續記錄若有一致欄位與狀態，團隊才可減少重複追問和人手核對。' },
    ],
  },
  'local-service-website': {
    painIntro: '本地上門服務的客人通常急於比較範圍、地區、收費和聯絡方法，頁面太含糊便容易流失。',
    painPoints: [
      { title: '服務寫得太闊', body: '只寫「專業服務」而沒有具體項目，客人不知道是否處理自己的情況。' },
      { title: '地區和收費沒有方向', body: '客人要先問幾輪基本資料，亦容易產生不合適查詢。' },
      { title: '手機聯絡多一步', body: '電話和 WhatsApp 不易找到，急需服務的客人會直接返回搜尋結果。' },
    ],
    comparison: [
      { aspect: '服務內容', self: '把宣傳單張或公司簡介直接放上網', doq: '按客人情況拆服務、流程、收費和 FAQ' },
      { aspect: '本地搜尋', self: '只在頁底列出地址或服務地區', doq: '把地區、服務範圍和頁面標題一起規劃' },
      { aspect: '查詢流程', self: '只放一個聯絡電話', doq: '安排 WhatsApp、電話和查詢前所需資料' },
    ],
    cases: [
      { title: '文仔清拆', slug: 'man-demolition', body: '集中交代住宅、寫字樓、商舖和餐廳清拆還原，並提供清晰報價和 WhatsApp 入口。' },
      { title: '文仔水喉', slug: 'man-plumber', body: '讓緊急維修客人快速看到服務範圍、收費方向和聯絡方法。' },
    ],
  },
  'professional-service-website': {
    painIntro: '專業服務網站常見問題不是資料太少，而是資歷、術語和服務說明太多，客人仍不知道你能否處理他的情況。',
    painPoints: [
      { title: '公司簡介像履歷', body: '資歷很多，但未說明適合哪些客人、處理甚麼問題和合作方式。' },
      { title: '專業術語太集中', body: '內行人看得懂，第一次接觸服務的決策者卻難以比較。' },
      { title: '多項服務沒有分頁', body: '不同需要都落在同一頁，客人和搜尋引擎都難掌握重點。' },
    ],
    comparison: [
      { aspect: '內容出發點', self: '由公司歷史、資歷和完整服務清單開始', doq: '先回應客人問題，再安排證明和專業背景' },
      { aspect: '服務架構', self: '所有專業內容集中在少數長頁面', doq: '按服務和決策階段拆頁，建立清楚內鏈' },
      { aspect: '查詢準備', self: '只放聯絡表單，讓客人自行描述', doq: '用流程和 FAQ 先解答合作前的主要疑問' },
    ],
    cases: [
      { title: '舒怡機電', slug: 'shu-yee-engineering', body: '重整公司形象和服務介紹，示範企業網站如何把多項專業服務分層呈現。' },
      { title: '正EV', slug: 'plus-ev', body: '把較新的服務概念寫成容易理解的資料，再以清楚入口承接查詢。' },
    ],
  },
  'google-ads-landing-page': {
    painIntro: 'Landing Page 的任務不是介紹整間公司，而是讓一批帶著特定問題入站的客人，快速判斷服務和下一步。',
    painPoints: [
      { title: '一頁同時推太多服務', body: '廣告只有一個重點，頁面卻要求客人自己再揀一次，容易失去焦點。' },
      { title: '廣告承諾和頁面不一致', body: '客人點擊某項服務，首屏卻先看到公司歷史或其他產品。' },
      { title: '只有按鈕，沒有追蹤', body: '不知道 WhatsApp、電話和表單由哪個廣告或哪個版本帶來。' },
    ],
    comparison: [
      { aspect: '頁面目標', self: '由現有首頁刪減內容後直接投放', doq: '按單一服務、受眾和查詢動作重新編排' },
      { aspect: '訊息配合', self: '所有廣告共用同一套標題和頁面', doq: '讓關鍵字、素材、首屏和 CTA 保持一致' },
      { aspect: '測試基礎', self: '只看平台顯示的點擊和轉化', doq: '先設定主要事件，再比較查詢數量和質素' },
    ],
    cases: [
      { title: '正EV', slug: 'plus-ev', body: '集中說明新服務和查詢下一步，適合承接搜尋、社交和短期推廣流量。' },
      { title: '文仔清拆', slug: 'man-demolition', body: '用具體服務範圍、報價方向和 WhatsApp 入口，減少廣告訪客再找資料的步驟。' },
    ],
  },
  'website-redesign': {
    painIntro: '舊網站重整最怕只換外觀，卻把原有內容、搜尋流量和客人已習慣的查詢方式一併打亂。',
    painPoints: [
      { title: '只重做視覺', body: '內容次序和服務說法沒有改，換了版面仍然解決不到查詢問題。' },
      { title: '沒有盤點舊 URL', body: '直接刪頁或改網址，原有搜尋入口和外部連結可能失效。' },
      { title: '所有舊內容照搬', body: '過時、重複和沒有作用的資料一起移到新網站，結構仍然混亂。' },
    ],
    comparison: [
      { aspect: '改版前', self: '按印象決定哪些頁面留下或刪除', doq: '先盤點內容、URL、手機版、SEO 和查詢入口' },
      { aspect: '改版中', self: '先完成新外觀，再補回舊資料', doq: '同步重排架構、文案、視覺和手機體驗' },
      { aspect: '上線時', self: '直接換站後逐個找問題', doq: '預先檢查轉址、連結、追蹤和重要頁面' },
    ],
    cases: [
      { title: '舒怡機電', slug: 'shu-yee-engineering', body: '針對舊網站內容較散的情況，重新整理公司形象、服務介紹和手機閱讀。' },
      { title: '文仔冷氣', slug: 'man-aircon', body: '把安裝、維修、清洗、保養、案例和評價重新集中成較清楚的本地服務網站。' },
    ],
  },
};

const serviceFaqAdditions: Record<string, Array<{ q: string; a: string }>> = {
  'web-design': [
    { q: '本地服務、專業公司和新品牌的設計重點有甚麼不同？', a: '本地服務要先講範圍、地區、收費和即時聯絡；專業公司要建立資歷、流程和案例信任；新品牌則要先把服務概念和適合對象說清楚。' },
    { q: '單頁網站和多頁公司網站應該怎樣選？', a: '只推一項服務或測試市場可先做單頁；有多項服務、需要 SEO、案例、文章或後臺更新，較適合多頁公司網站。' },
    { q: '網頁設計價錢會受甚麼影響？', a: '主要取決於頁數、文案準備程度、語言、CMS、表單或其他功能。正式報價會逐項列出，不會只用外觀估價。' },
  ],
  seo: [
    { q: 'SEO 和廣告要不要一起規劃？', a: '建議至少一起規劃頁面和追蹤。廣告可以較快測試搜尋字和訊息，SEO 則持續累積內容；兩者共用清楚的服務頁，可減少重複製作。' },
    { q: '只寫博客文章可以做到 SEO 嗎？', a: '通常不夠。文章負責回答問題，服務頁負責交代你提供甚麼、適合誰和怎樣查詢；技術、內鏈和持續更新亦同樣重要。' },
    { q: '停止月費 SEO 後，排名會立即消失嗎？', a: '不會因停止服務而立即消失，但競爭對手、內容過時和技術問題仍會令表現變化。已完成的內容和設定應保留在你的網站及帳戶。' },
  ],
  'google-meta-ads': [
    { q: '廣告流量落首頁還是獨立 Landing Page 較好？', a: '如果只推一項服務，獨立 Landing Page 通常較集中；品牌搜尋或需要比較多項服務時，首頁或相關服務頁亦可以。要按搜尋意圖和廣告訊息決定。' },
    { q: 'Google Ads 和 Meta 廣告應該先做哪個？', a: '客人已有明確搜尋需要，可先考慮 Google Ads；需要展示新概念、視覺素材或主動接觸受眾，可考慮 Meta。部分項目會用兩者分工。' },
    { q: '怎樣判斷廣告查詢是否有質素？', a: '除了轉化數字，還要記錄服務是否合適、地區、預算、是否能聯絡和最後有否成交，否則平台只知道有人按過按鈕。' },
  ],
  'erp-system-development': [
    { q: '應該先用現成軟件還是做 ERP 系統訂製？', a: '流程接近標準功能、想快速開始時，現成軟件通常較合適；若關鍵流程、角色權限、資料規則或整合要求難以配合，才值得評估訂製。' },
    { q: 'ERP 系統第一階段應該先做甚麼？', a: '先選最影響營運的流程，例如客戶與報價、訂單與項目、庫存、出勤更表或審批。第一階段要能真正被團隊使用，再按資料和回饋擴充。' },
    { q: '系統上線後是否有持續支援？', a: '有。可按需要安排使用培訓、錯誤處理、功能迭代、整合調整和系統改善；實際回應和服務範圍會在報價或合約列明。' },
    { q: '可以處理更表和出糧嗎？', a: '可以按公司規則整合員工檔案、出勤、更表、假期、加班和出糧所需資料、審批與匯出流程。法定薪酬、稅務、強積金或會計計算規則須由你的會計、HR 或相關專業人士確認。' },
  ],
  'local-service-website': [
    { q: '本地服務網站需要做地區頁嗎？', a: '只有在不同地區確實有不同服務資料時才值得做。大量只有地名不同的重複頁面，對客人和 SEO 都沒有實際幫助。' },
    { q: '沒有固定價錢，網站可以怎樣寫收費？', a: '可以說明起步價、計價方式、影響報價的因素，以及客人查詢前要提供的相片、尺寸或地點資料。' },
    { q: '可否讓客人直接在 WhatsApp 傳相問價？', a: '可以。頁面可以預先提示需要的相片和資料，再用 WhatsApp CTA 接續，減少來回追問。' },
  ],
  'professional-service-website': [
    { q: '專業服務網站應該先講公司還是先講客人問題？', a: '通常先讓客人確認你能否處理他的問題，再以團隊、資歷、流程和案例證明能力；公司歷史不需要完全刪走，但不應阻礙判斷。' },
    { q: '案例涉及保密資料，可以怎樣處理？', a: '可以匿名描述客戶類型、問題、處理範圍和可公開結果，或改用流程、常見情況和行業經驗建立信任。' },
    { q: 'B2B 網站需要直接公開價錢嗎？', a: '不一定。可以先交代計價方式、合作範圍和影響報價的因素，讓決策者知道是否適合再安排查詢。' },
  ],
  'google-ads-landing-page': [
    { q: '一個 Landing Page 可以同時放多項服務嗎？', a: '可以放相關內容，但最好只有一個主要推廣目標。如果服務對象、搜尋意圖或價錢差別很大，應分開頁面。' },
    { q: 'Landing Page 需要放 FAQ 和案例嗎？', a: '通常需要。客人未必會再瀏覽整個網站，FAQ 和案例可在同一頁處理主要疑問和信任問題。' },
    { q: '頁面上線後還需要修改嗎？', a: '需要按搜尋字、受眾、點擊、捲動和查詢質素判斷。標題、內容次序、CTA 和表單都可能要經過測試。' },
  ],
  'website-redesign': [
    { q: '怎樣判斷只改版還是整站重做？', a: '先看現有平台是否穩定、後臺能否更新、手機版是否可用，以及內容和 URL 有多少值得保留。技術限制太多時，整站重做通常較合理。' },
    { q: '改版前需要做哪些 SEO 準備？', a: '應盤點有流量和外部連結的頁面、現有 URL、標題、索引狀況和重要內容，並為改網址的頁面安排轉址。' },
    { q: '改版期間網站需要停機嗎？', a: '一般會在獨立環境製作和測試，正式切換才需要短時間處理。實際安排視乎網域、寄存和舊平台限制。' },
  ],
};

const servicesBySlug = new Map(
  services.map((service) => [
    service.slug,
    {
      ...service,
      faqs: [...service.faqs, ...(serviceFaqAdditions[service.slug] ?? [])],
    },
  ]),
);
const serviceSlugAliases: Record<string, string> = {
  'google-meta-ads': 'google-ads',
  'facebook-ads': 'meta-ads',
};
const serviceIconBySlug = {
  'web-design': LayoutTemplate,
  seo: Search,
  'google-ads': BarChart3,
  'meta-ads': CircleDollarSign,
  'google-meta-ads': CircleDollarSign,
  'whatsapp-chatbot-development': MessageCircle,
  'erp-system-development': ShieldCheck,
  'local-service-website': MapPin,
  'professional-service-website': ShieldCheck,
  'google-ads-landing-page': CircleDollarSign,
  'website-redesign': FileSearch,
} satisfies Record<string, typeof LayoutTemplate>;

export const Route = createFileRoute('/services/$slug')({
  loader: ({ params }) => {
    const service = servicesBySlug.get(serviceSlugAliases[params.slug] ?? params.slug);
    if (!service) throw notFound();
    return service;
  },
  head: ({ loaderData }) => {
    const baseUrl = canonicalSiteUrl;
    const serviceUrl = `${baseUrl}/services/${loaderData.slug}`;
    const ogImageUrl = `${baseUrl}/og-image.jpg`;
    return {
      meta: [
        { title: loaderData.metaTitle },
        { name: 'description', content: loaderData.metaDescription },
        { property: 'og:title', content: loaderData.metaTitle },
        { property: 'og:description', content: loaderData.metaDescription },
        { property: 'og:type', content: 'website' },
        { property: 'og:url', content: serviceUrl },
        { property: 'og:image', content: ogImageUrl },
        { property: 'og:image:width', content: '1200' },
        { property: 'og:image:height', content: '630' },
        { property: 'og:image:alt', content: '渡橋 Doq Creative 香港網頁設計與 SEO 預覽圖' },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: loaderData.metaTitle },
        { name: 'twitter:description', content: loaderData.metaDescription },
        { name: 'twitter:image', content: ogImageUrl },
        { name: 'twitter:image:alt', content: '渡橋 Doq Creative 香港網頁設計與 SEO 預覽圖' },
        {
          'script:ld+json': {
            '@context': 'https://schema.org',
            '@graph': [
              {
                '@type': 'Service',
                '@id': `${serviceUrl}#service`,
                name: loaderData.title,
                description: loaderData.metaDescription,
                url: serviceUrl,
                areaServed: { '@type': 'AdministrativeArea', name: 'Hong Kong' },
                provider: {
                  '@type': 'ProfessionalService',
                  '@id': `${baseUrl}/#organization`,
                  name: '渡橋 Doq Creative',
                  url: `${baseUrl}/`,
                  email: emailAddress,
                  telephone: '+85295946956',
                },
                serviceType: loaderData.title,
              },
              {
                '@type': 'FAQPage',
                '@id': `${serviceUrl}#faq`,
                inLanguage: 'zh-HK',
                mainEntity: loaderData.faqs.map((faq) => ({
                  '@type': 'Question',
                  name: faq.q,
                  acceptedAnswer: { '@type': 'Answer', text: faq.a },
                })),
              },
              {
                '@type': 'BreadcrumbList',
                '@id': `${serviceUrl}#breadcrumb`,
                itemListElement: [
                  { '@type': 'ListItem', position: 1, name: '首頁', item: `${baseUrl}/` },
                  { '@type': 'ListItem', position: 2, name: '服務', item: `${baseUrl}/#services` },
                  { '@type': 'ListItem', position: 3, name: loaderData.title, item: serviceUrl },
                ],
              },
            ],
          },
        },
      ],
      links: [
        { rel: 'canonical', href: serviceUrl },
        { rel: 'alternate', hrefLang: 'zh-HK', href: serviceUrl },
        { rel: 'alternate', hrefLang: 'en', href: `${baseUrl}/en/services/${loaderData.slug}` },
        { rel: 'alternate', hrefLang: 'x-default', href: serviceUrl },
      ],
    };
  },
  component: ServicePage,
});

function ServicePage() {
  const service = Route.useLoaderData();

  return (
    <div className="min-h-screen bg-[#f4efe4] text-[#102d28]">
      <div className="mx-auto max-w-[1540px] border-x border-[#143d35]/16 bg-[#f8f3e9] shadow-[0_0_0_1px_rgba(20,61,53,0.04)]">
        <PublicSiteHeader locale="zh" active="services" enHref={`/en/services/${service.slug}`} zhHref={`/services/${service.slug}`} />
        <main id="main-content">
          <Hero service={service} />
          <PainPointsSection service={service} />
          <FitSection service={service} />
          <DeliverablesSection service={service} />
          <QuoteFactorsSection service={service} />
          <PlanningSection service={service} />
          <ComparisonSection service={service} />
          <RelatedCasesSection service={service} />
          <ProcessSection service={service} />
          <FaqSection service={service} />
          <ContactSection
            locale="zh"
            title={`想了解${service.title}點樣做？`}
            description="可以先傳現有網站、參考網站，或者簡單講你想推的服務。我哋會先幫你睇方向，再建議應該由網站、內容、SEO 還是 Landing Page 開始。"
          />
        </main>
        <Footer />
      </div>
      <FloatingWhatsApp />
    </div>
  );
}

function Hero({ service }: { service: ServicePage }) {
  const Icon = serviceIconBySlug[service.slug] ?? LayoutTemplate;

  return (
    <section className="relative overflow-hidden border-b border-[#143d35]/14 px-5 py-16 sm:px-8 lg:px-24 lg:py-20">
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,rgba(20,61,53,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(20,61,53,0.08)_1px,transparent_1px)] bg-[size:58px_58px]" />
      <div className="relative z-10 grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-end">
        <div>
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold text-[#173d35] hover:text-[#815321]">
            <ArrowLeft className="size-4" />
            返回服務
          </Link>
          <p className="mt-12 text-sm font-semibold uppercase tracking-[0.18em] text-[#815321]">{service.eyebrow}</p>
          <h1 className="mt-5 max-w-3xl text-balance text-5xl font-semibold leading-[1.08] tracking-[-0.075em] text-[#063d36] sm:text-6xl lg:text-7xl">
            {service.title}
          </h1>
          <p className="mt-8 max-w-2xl text-lg leading-9 tracking-[-0.02em] text-[#324740]">{service.intro}</p>
          <WhatsAppButton className="mt-10 h-14 px-7 text-base" />
        </div>
        <div className="border border-[#143d35]/18 bg-[#fbf7ef]/78 p-7 sm:p-9">
          <div className="flex items-center justify-between border-b border-[#143d35]/14 pb-6">
            <Icon className="size-10 text-[#063d36]" strokeWidth={1.45} />
            <span className="text-sm font-semibold text-[#815321]">{service.accent}</span>
          </div>
          <div className="mt-8 grid gap-px bg-[#143d35]/14 sm:grid-cols-2">
            {service.deliverables.slice(0, 4).map((item) => (
              <div key={item} className="min-h-[112px] bg-[#f8f3e9] p-5">
                <Check className="size-5 text-[#815321]" strokeWidth={1.8} />
                <p className="mt-4 text-sm leading-6 text-[#354a43]">{item}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 text-sm leading-7 text-[#344840]">
            每個服務頁都會按你的行業、客人搜尋方式和查詢流程再調整，不用硬套模板。
          </p>
        </div>
      </div>
    </section>
  );
}

function PainPointsSection({ service }: { service: ServicePage }) {
  const content = serviceDecisionContent[service.slug];

  return (
    <section className="border-b border-[#143d35]/14 bg-[#f1eadc] px-5 py-12 sm:px-8 lg:px-24">
      <div className="grid gap-9 lg:grid-cols-[0.72fr_1.28fr]">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#815321]">Common problems</p>
          <h2 className="mt-4 text-balance text-4xl font-semibold leading-tight tracking-[-0.05em]">客人通常卡在哪裡？</h2>
          <p className="mt-5 max-w-lg text-sm leading-7 text-[#344840]">{content.painIntro}</p>
        </div>
        <div className="grid gap-px bg-[#143d35]/14 md:grid-cols-3">
          {content.painPoints.map((item, index) => (
            <article key={item.title} className="bg-[#fbf7ef] p-6">
              <span className="text-sm font-semibold text-[#815321]">{String(index + 1).padStart(2, '0')}</span>
              <h3 className="mt-6 text-lg font-semibold tracking-[-0.03em]">{item.title}</h3>
              <p className="mt-3 text-sm leading-7 text-[#344840]">{item.body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function FitSection({ service }: { service: ServicePage }) {
  return (
    <section className="border-b border-[#143d35]/14 px-5 py-12 sm:px-8 lg:px-24">
      <div className="grid gap-10 lg:grid-cols-[0.78fr_1.22fr]">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#815321]">Good fit</p>
          <h2 className="mt-4 text-balance text-4xl font-semibold leading-tight tracking-[-0.05em]">
            適合以下情況
          </h2>
        </div>
        <div className="grid gap-px bg-[#143d35]/14 sm:grid-cols-2">
          {service.fit.map((item) => (
            <div key={item} className="bg-[#fbf7ef]/76 p-6">
              <Target className="size-6 text-[#815321]" strokeWidth={1.6} />
              <p className="mt-6 font-semibold tracking-[-0.03em]">{item}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const standardDeliverables = [
  'Sitemap / 頁面架構建議',
  '文案次序和內容重點整理',
  '手機版與桌面版製作',
  'SEO title、meta description 和基本頁面設定',
  'WhatsApp 查詢 CTA',
  '基本追蹤設定建議',
  '一年網站寄存',
];

function getDeliverables(service: ServicePage) {
  if (service.slug === 'seo' || service.slug === 'google-ads' || service.slug === 'meta-ads' || service.slug === 'google-meta-ads' || service.slug === 'google-ads-landing-page' || service.slug === 'whatsapp-chatbot-development' || service.slug === 'erp-system-development') {
    return service.deliverables;
  }

  return Array.from(new Set([...service.deliverables, ...standardDeliverables]));
}

function DeliverablesSection({ service }: { service: ServicePage }) {
  const items = getDeliverables(service);
  const isErp = service.slug === 'erp-system-development';
  const isSeo = service.slug === 'seo';

  return (
    <section className="border-b border-[#143d35]/14 px-5 py-12 sm:px-8 lg:px-24">
      <div className="grid gap-10 lg:grid-cols-[0.72fr_1.28fr] lg:items-start">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#815321]">What you get</p>
          <h2 className="mt-4 text-balance text-4xl font-semibold leading-tight tracking-[-0.05em]">
            {isErp ? 'ERP 系統可交付的範圍' : isSeo ? 'SEO 服務工作清單' : '交付內容清單'}
          </h2>
          <p className="mt-5 max-w-md text-sm leading-7 text-[#344840]">
            {isErp ? '由流程梳理、原型和核心模組開發，到測試、上線及後續迭代；實際範圍會按功能、角色權限、第三方整合和資料遷移需要確認。' : isSeo ? 'SEO 範圍會按現有網站、主要服務、目標地區、競爭程度和內容基礎確認。以下項目是持續搜尋優化常見的工作，不包括網站製作或寄存服務。' : '報價前會先確認範圍。下面是一般會交付的內容，實際項目會按頁面數量、功能和你手上已有的資料調整。'}
          </p>
        </div>
        <div className="grid gap-px bg-[#fbf7ef]/76 sm:grid-cols-2 xl:grid-cols-3">
          {items.map((item) => (
            <div key={item} className="min-h-[126px] bg-[#fbf7ef]/76 p-5">
              <Check className="size-5 text-[#815321]" strokeWidth={1.8} />
              <p className="mt-4 text-sm font-medium leading-6 text-[#263a34]">{item}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const quoteFactors = [
  {
    title: '頁面數量',
    body: '一頁 Landing Page、幾頁公司網站，或者十多頁服務網站，工作量差很遠。頁面愈多，內容整理、設計和測試時間也會增加。',
  },
  {
    title: '文案準備程度',
    body: '如果你已有清楚文字和圖片，製作會快很多；如果要重新整理服務說法、FAQ 和 CTA，會另外計入文案規劃時間。',
  },
  {
    title: 'CMS 後臺',
    body: '需要自己更新文章、案例、服務或相片，就要加入後臺管理。只做靜態頁面，成本會低一點。',
  },
  {
    title: '多語言版本',
    body: '繁中、英文或簡中不只是翻譯文字，頁面標題、URL、SEO meta 和版面長度都要一起處理。',
  },
  {
    title: '追蹤和轉化設定',
    body: '如果要追 WhatsApp、表單、電話、Google Ads 轉化或 GA4 事件，需要在上線前先定好查詢路徑。',
  },
];

const erpQuoteFactors = [
  { title: '核心流程與模組', body: '先確定要處理銷售、訂單、庫存、項目、員工、出勤、更表、出糧資料或審批中的哪些流程。第一階段功能愈清楚，報價愈準確。' },
  { title: '角色與權限', body: '不同部門、主管和前線人員可看的資料、可做的動作和審批責任不同，權限規則會影響系統設計與測試。' },
  { title: '資料遷移', body: 'Excel、舊系統或多份人手記錄需要先盤點欄位、重複資料、歷史資料和匯入方式，才能評估清理與驗證工作。' },
  { title: '第三方整合', body: '付款、短信、電郵、會計、考勤裝置或現有 API 等整合，會按可用介面、資料格式和測試範圍另行確認。' },
  { title: '上線與持續迭代', body: '培訓、上線支援、後續模組、報表和流程調整會按實際使用安排，讓系統可以逐步擴充。' },
];

const whatsappChatbotQuoteFactors = [
  { title: '對話流程與語言', body: '要處理多少種服務問題、分流路徑和繁中或英文對話，會影響內容梳理、配置和測試範圍。' },
  { title: '資料收集與預約規則', body: '地點、服務類型、人數、時段、緩衝時間、確認、改期和取消規則愈具體，流程設計和測試愈需要逐項確認。' },
  { title: '人工轉接與通知', body: '甚麼情況要交給真人、通知哪位同事、要附上哪些資料，以及團隊如何查看歷史記錄，都需要先定清楚。' },
  { title: '第三方平台與 API 整合', body: 'WhatsApp Business API、日曆、CRM、雲端或其他工具是否有可用介面、帳戶權限和資料格式，會影響開發與測試範圍。' },
  { title: '測試、上線與後續迭代', body: '錯誤輸入、改期、超時、人工接手和實際客戶問題都需要測試；上線後的新 FAQ 或功能會按階段另行確認。' },
];

const googleAdsQuoteFactors = [
  { title: '推廣目標與服務範圍', body: '先確認想推哪一項服務、地區、客人問題和不承接項目，才可安排 Campaign 與關鍵字範圍。' },
  { title: '關鍵字與 Campaign 數量', body: '品牌字、服務字、地區字和不同服務通常需要分組管理；範圍愈多，建立和持續檢視的工作愈多。' },
  { title: 'Landing Page 與轉化追蹤', body: '廣告要導去首頁、服務頁還是獨立 Landing Page，以及 WhatsApp、電話、表單和 GA4 轉化怎樣追蹤，都會影響設定範圍。' },
  { title: '現有帳戶與資料狀況', body: '新開帳戶與接手舊帳戶的工作不同；舊 Campaign、搜尋字詞、標籤和帳戶權限需要先檢視。' },
  { title: '優化節奏與廣告媒體費', body: '代營運月費按檢視、測試和報告節奏確認；Google 媒體費直接支付給平台，會與管理費分開列明。' },
];

const metaAdsQuoteFactors = [
  { title: '推廣目標與受眾階段', body: '新客認識、網站再營銷、即時表單或查詢目標需要不同 Campaign、受眾和訊息安排。' },
  { title: '廣告渠道、Campaign 與版位', body: 'Facebook、Instagram、Threads、冷受眾與再營銷是否分開管理，會影響設定、預算分配和優化工作。' },
  { title: '素材製作與測試範圍', body: '平面、短片、拍攝、剪輯和每月要測試的不同訊息角度，應與帳戶管理工作分開確認。' },
  { title: 'Landing Page、表單與 Pixel 追蹤', body: '流量導向網站還是即時表單、Meta Pixel、WhatsApp、電話和表單事件怎樣量度，都會影響設定範圍。' },
  { title: '優化節奏與廣告媒體費', body: '代營運月費按受眾、素材、版位和查詢資料的檢視節奏確認；Meta 媒體費直接支付給平台，會另行列明。' },
];

function QuoteFactorsSection({ service }: { service: ServicePage }) {
  const isErp = service.slug === 'erp-system-development';
  const isGoogleAds = service.slug === 'google-ads';
  const isMetaAds = service.slug === 'meta-ads';
  const isWhatsAppChatbot = service.slug === 'whatsapp-chatbot-development';
  const factors = isErp ? erpQuoteFactors : isGoogleAds ? googleAdsQuoteFactors : isMetaAds ? metaAdsQuoteFactors : isWhatsAppChatbot ? whatsappChatbotQuoteFactors : quoteFactors;
  return (
    <section className="border-b border-[#143d35]/14 bg-[#f1eadc] px-5 py-12 sm:px-8 lg:px-24">
      <div className="mb-9 flex flex-col justify-between gap-4 md:flex-row md:items-end">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#815321]">Before quote</p>
          <h2 className="mt-4 text-balance text-4xl font-semibold leading-tight tracking-[-0.05em]">
            {isErp ? 'ERP 報價會按甚麼釐定' : isGoogleAds ? 'Google Ads 報價通常會睇呢幾件事' : isMetaAds ? 'Meta 廣告報價通常會睇呢幾件事' : isWhatsAppChatbot ? 'WhatsApp Chatbot 開發報價會睇甚麼' : '報價通常會睇呢幾件事'}
          </h2>
        </div>
        <p className="max-w-md text-sm leading-7 text-[#344840]">
          {isErp ? 'ERP 不是按固定頁數收費，而是按實際營運流程、模組、使用角色、資料遷移、整合和上線安排逐項確認。先梳理清楚，才不會開發到一半才發現漏了關鍵流程。' : isGoogleAds ? 'Google Ads 代營運不是按固定頁數計價，而是按要推的服務、關鍵字與 Campaign 數量、承接頁面、追蹤和優化節奏確認。媒體費會與管理費分開列明。' : isMetaAds ? 'Meta 廣告代營運不是只按一個貼文或版位計價，而是按推廣目標、受眾、素材、Campaign、追蹤和優化節奏確認。媒體費與素材製作費會按需要另列。' : isWhatsAppChatbot ? 'WhatsApp Chatbot 開發不是固定模板或單一月費。對話、預約、人工轉接和第三方整合會按實際業務流程確認；平台、雲端或 API 的持續費用會與開發工作分開列明。' : '同一個「網站設計」要求，實際可以很簡單，也可以牽涉內容、後臺和廣告追蹤。先拆清楚，報價會實在得多。'}
        </p>
      </div>
      <div className="grid gap-4 md:grid-cols-5">
        {factors.map((factor, index) => (
          <article key={factor.title} className="border border-[#143d35]/18 bg-[#fbf7ef]/82 p-5">
            <span className="text-sm font-semibold text-[#815321]">{String(index + 1).padStart(2, '0')}</span>
            <h3 className="mt-7 text-lg font-semibold tracking-[-0.04em] text-[#102d28]">{factor.title}</h3>
            <p className="mt-3 text-sm leading-7 text-[#344840]">{factor.body}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function PlanningSection({ service }: { service: ServicePage }) {
  const isErp = service.slug === 'erp-system-development';
  return (
    <section className="border-b border-[#143d35]/14 px-5 py-12 sm:px-8 lg:px-24">
      <div className="mb-9 flex flex-col justify-between gap-4 md:flex-row md:items-end">
        <h2 className="max-w-2xl text-balance text-4xl font-semibold leading-tight tracking-[-0.05em]">
          {isErp ? '開始 ERP 開發前，先梳理營運流程' : '做之前，先度清楚重點'}
        </h2>
        <p className="max-w-md text-sm leading-7 text-[#344840]">
          {isErp ? '我們會先看每個角色怎樣收資料、安排更表、處理銷售或訂單、交接工作和批核，才決定系統第一階段應該怎樣做。' : '我們會先看服務、客人、內容和查詢路徑，再決定頁面應該怎樣做。'}
        </p>
      </div>
      <div className="grid gap-5 md:grid-cols-3">
        {service.planning.map((item, index) => (
          <article key={item.title} className="min-h-[260px] border border-[#143d35]/18 bg-[#fbf7ef]/75 p-7">
            <span className="text-sm font-semibold text-[#815321]">{String(index + 1).padStart(2, '0')}</span>
            <h3 className="mt-8 text-xl font-semibold tracking-[-0.04em]">{item.title}</h3>
            <p className="mt-4 text-sm leading-7 text-[#344840]">{item.body}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function ComparisonSection({ service }: { service: ServicePage }) {
  const content = serviceDecisionContent[service.slug];

  return (
    <section className="border-b border-[#143d35]/14 px-5 py-12 sm:px-8 lg:px-24">
      <div className="grid gap-8 lg:grid-cols-[0.7fr_1.3fr]">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#815321]">Compare</p>
          <h2 className="mt-4 text-balance text-4xl font-semibold leading-tight tracking-[-0.05em]">自己處理 vs 找渡橋</h2>
          <p className="mt-5 max-w-lg text-sm leading-7 text-[#344840]">自己做適合有時間研究和執行的團隊；找渡橋則把內容、頁面和查詢目標放在同一個工作範圍內。沒有一個做法適合所有公司，可按時間和內部能力選擇。</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[660px] border-collapse text-left text-sm">
            <thead><tr className="border-b-2 border-[#143d35]"><th className="py-4 pr-5">比較項目</th><th className="px-5 py-4">自己處理</th><th className="py-4 pl-5">找渡橋</th></tr></thead>
            <tbody className="divide-y divide-[#143d35]/14">
              {content.comparison.map((row) => (
                <tr key={row.aspect}>
                  <th className="py-5 pr-5 align-top font-semibold text-[#102d28]">{row.aspect}</th>
                  <td className="px-5 py-5 align-top leading-7 text-[#344840]">{row.self}</td>
                  <td className="py-5 pl-5 align-top leading-7 text-[#344840]">{row.doq}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function RelatedCasesSection({ service }: { service: ServicePage }) {
  if (service.slug === 'whatsapp-chatbot-development') {
    return <section className="border-b border-[#143d35]/14 bg-[#f1eadc] px-5 py-12 sm:px-8 lg:px-24"><div className="grid gap-8 lg:grid-cols-[0.72fr_1.28fr]"><div><p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#815321]">Related services</p><h2 className="mt-4 text-4xl font-semibold tracking-[-0.05em]">Chat Bot 要接到清楚的查詢流程</h2><p className="mt-5 max-w-xl text-sm leading-7 text-[#344840]">機器人適合處理重複問題和預約資料；網站、Landing Page 與 ERP 則負責解釋服務、承接流量或跟進後續營運資料。</p></div><div className="grid gap-px bg-[#fbf7ef] sm:grid-cols-3"><Link to="/services/google-ads-landing-page" className="bg-[#fbf7ef] p-6 text-sm font-semibold leading-6 hover:text-[#815321]">服務頁與 Landing Page<ArrowRight className="mt-4 size-4" /></Link><Link to="/services/erp-system-development" className="bg-[#fbf7ef] p-6 text-sm font-semibold leading-6 hover:text-[#815321]">ERP 營運系統訂製<ArrowRight className="mt-4 size-4" /></Link><Link to="/pricing" className="bg-[#fbf7ef] p-6 text-sm font-semibold leading-6 hover:text-[#815321]">查看服務報價原則<ArrowRight className="mt-4 size-4" /></Link></div></div></section>;
  }
  if (service.slug === 'erp-system-development') {
    return <section className="border-b border-[#143d35]/14 bg-[#f1eadc] px-5 py-12 sm:px-8 lg:px-24"><div className="grid gap-8 lg:grid-cols-[0.72fr_1.28fr]"><div><p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#815321]">ERP modules</p><h2 className="mt-4 text-4xl font-semibold tracking-[-0.05em]">先由最影響營運的流程開始</h2><p className="mt-5 max-w-xl text-sm leading-7 text-[#344840]">每間公司的第一階段不同。先確定一條會被團隊每天使用的流程，才決定下一步擴充。</p></div><ul className="grid gap-px bg-[#143d35]/14 sm:grid-cols-2">{['客戶、報價與訂單', '庫存、採購與付款狀態', '項目、任務與交付進度', '員工檔案、出勤與更表', '出糧資料、審批與記錄', '角色權限與管理報表'].map((item) => <li key={item} className="bg-[#fbf7ef] p-6 text-sm font-semibold leading-6">{item}</li>)}</ul></div></section>;
  }
  const content = serviceDecisionContent[service.slug];

  return (
    <section className="border-b border-[#143d35]/14 bg-[#f1eadc] px-5 py-12 sm:px-8 lg:px-24">
      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#815321]">Related cases</p>
          <h2 className="mt-4 text-4xl font-semibold tracking-[-0.05em]">相關網站案例</h2>
        </div>
        <Link to="/cases" className="inline-flex items-center gap-2 text-sm font-semibold text-[#173d35] hover:text-[#815321]">查看全部案例<ArrowRight className="size-4" /></Link>
      </div>
      <div className="mt-8 grid gap-px bg-[#143d35]/14 md:grid-cols-2">
        {content.cases.map((item) => (
          <article key={item.title} className="bg-[#fbf7ef] p-6 sm:p-8">
            <h3 className="text-xl font-semibold tracking-[-0.03em]">{item.title}</h3>
            <p className="mt-3 max-w-xl text-sm leading-7 text-[#344840]">{item.body}</p>
            <Link to={`/case/${item.slug}`} className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#815321]">查看案例詳情<ArrowRight className="size-4" /></Link>
          </article>
        ))}
      </div>
      <p className="mt-5 text-xs leading-5 text-[#52635c]">案例簡述只說明網站可見的內容和頁面安排，不代表未量度的流量、排名或廣告成效。</p>
    </section>
  );
}

const processReasons = [
  '先確認實際需要，避免用錯頁面或服務方向。',
  '先定範圍和次序，減少製作期間反覆重做。',
  '讓內容、桌面版、手機版和查詢入口同步完成。',
  '確認網站或推廣設定真正可用，再交付或持續優化。',
];

function ProcessSection({ service }: { service: ServicePage }) {
  return (
    <section className="border-b border-[#143d35]/14 px-5 py-12 sm:px-8 lg:px-24">
      <h2 className="text-4xl font-semibold tracking-[-0.05em]">流程</h2>
      <div className="mt-10 grid gap-6 md:grid-cols-4">
        {service.process.map((step, index) => (
          <article key={step.title} className="relative border-t border-[#143d35]/35 pt-7">
            {index < service.process.length - 1 ? (
              <span className="absolute right-0 top-[-1px] hidden h-px w-1/2 bg-[#143d35]/35 md:block" />
            ) : null}
            <span className="grid size-8 place-items-center rounded-full bg-[#063d36] text-xs font-semibold text-[#fbf5ea]">
              {String(index + 1).padStart(2, '0')}
            </span>
            <h3 className="mt-6 text-lg font-semibold tracking-[-0.03em]">{step.title}</h3>
            <p className="mt-3 text-sm leading-7 text-[#344840]">{step.body}</p>
            <p className="mt-3 text-xs leading-5 text-[#52635c]"><span className="font-semibold text-[#815321]">為甚麼：</span>{processReasons[index]}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function FaqSection({ service }: { service: ServicePage }) {
  return (
    <section className="border-b border-[#143d35]/14 px-5 py-12 sm:px-8 lg:px-24">
      <h2 className="text-4xl font-semibold tracking-[-0.05em]">常見問題</h2>
      <div className="mt-8 grid gap-5 md:grid-cols-3">
        {service.faqs.map((faq) => (
          <ServiceFaqItem
            key={faq.q}
            question={faq.q}
            answer={faq.a}
          />
        ))}
      </div>
    </section>
  );
}

function ServiceFaqItem({
  question,
  answer,
}: {
  question: string;
  answer: string;
}) {
  return (
    <details className="group self-start border border-[#143d35]/18 bg-[#fbf7ef]/68">
      <summary className="flex cursor-pointer list-none items-start justify-between gap-4 px-6 py-5 text-left font-semibold leading-7 tracking-[-0.03em] text-[#143d35] outline-none transition hover:bg-[#f3eadc] focus-visible:ring-2 focus-visible:ring-[#d9a267] focus-visible:ring-offset-2 focus-visible:ring-offset-[#fbf5ea] [&::-webkit-details-marker]:hidden">
        <span>{question}</span>
        <ChevronDown
          className="mt-1 size-5 shrink-0 text-[#815321] transition group-open:rotate-180"
          strokeWidth={1.6}
          aria-hidden="true"
        />
      </summary>
      <div className="px-6 pb-5 text-sm leading-7 text-[#344840]">
        {answer}
      </div>
    </details>
  );
}

function WhatsAppButton({ className = '' }: { className?: string }) {
  return (
    <a
      href={whatsappUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center justify-center gap-2 bg-[#815321] font-semibold text-white transition hover:bg-[#6f451c] active:translate-y-px ${className}`}
    >
      WhatsApp 查詢
      <ArrowRight className="size-4" strokeWidth={1.8} />
    </a>
  );
}

function FloatingWhatsApp() {
  return (
    <a
      href={whatsappUrl}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="WhatsApp 查詢"
      className="fixed bottom-5 right-5 z-50 grid size-14 place-items-center rounded-full bg-[#063d36] text-[#fbf5ea] shadow-[0_16px_34px_rgba(6,61,54,0.28)] transition hover:-translate-y-1 hover:bg-[#0b4f45]"
    >
      <MessageCircle className="size-6" strokeWidth={1.8} />
    </a>
  );
}

function Footer() {
  return (
    <footer className="border-t border-[#143d35]/14 px-5 py-6 sm:px-8 lg:px-24">
      <div className="flex flex-col gap-3 text-center text-sm text-[#344840] md:flex-row md:items-center md:justify-between md:text-left">
        <Link to="/" className="font-semibold text-[#102d28]">
          渡橋創意有限公司
        </Link>
        <div className="flex gap-4"><Link to="/terms" className="hover:text-[#815321]">服務條款</Link><Link to="/privacy" className="hover:text-[#815321]">隱私政策</Link></div>
        <p>© 2026 渡橋創意有限公司。保留所有權利。</p>
      </div>
    </footer>
  );
}
